import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * PropertyImportFlow — §0.6 / AC0.4 the onboarding WEDGE: the one flow that takes a property manager
 * from "I created a property and hit a dead end" to a populated building in a single action. POSTs to
 * the already-built POST /api/properties/bulk-import (PropertyService.bulkImport), which creates the
 * Property + N Units + a Lease per occupied row in ONE transaction and dedups each tenant against the
 * existing Contacts rolodex (match-or-create). A partial import is impossible by construction, so this
 * UI mirrors that: it never claims a half-success — it shows the server's exact outcome counts
 * (units/leases created, tenants matched vs created) and the skipped rows WITH their reasons.
 *
 * Shape: a full-viewport overlay (end-anchored panel on mobile, centered card on md+), the same
 * `fixed inset-0 z-50` idiom UnitDrillInOverlay uses. Deliberately NOT backdrop-dismissable — a bulk
 * form holds a lot of typed/pasted rows a stray outside-tap would discard; it closes only via the
 * explicit × / Cancel, and Escape (matching SheetShell's keydown rule) only while no result is shown.
 *
 * Owner-company fetch reuses PortfolioEmptyState's exact pattern (GET /api/companies, zero-company
 * guard with the same honest "add a company first" copy) so the two property-creation paths behave the
 * same way on a brand-new account.
 */

interface Company { id: string; name: string }

interface Row {
  unitLabel: string;
  tenantName: string;
  tenantEmail: string;
  tenantPhone: string;
  rent: string; // raw text; parsed to a number (or omitted) only at submit — never a fabricated $0
}

interface ImportResult {
  propertyId?: string;
  unitsCreated?: number;
  leasesCreated?: number;
  contactsCreated?: number;
  contactsMatched?: number;
  skipped?: Array<{ unitLabel: string; reason: string }>;
}

const emptyRow = (): Row => ({ unitLabel: '', tenantName: '', tenantEmail: '', tenantPhone: '', rent: '' });

// Parse a numeric rent out of free text ("$1,200" → 1200). Empty / non-numeric → null (the server
// then skips that tenant row WITH a reason rather than inventing a $0 lease).
const parseRent = (raw: string): number | null => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

// A spreadsheet paste often carries a header row ("Unit, Tenant, …"). Treat the first line as a header
// and drop it when it reads like column names AND has no numeric rent — never drop a real data row
// (a row whose rent column holds a digit is data, whatever it's named).
const HEADER_WORDS = ['unit', 'tenant', 'name', 'email', 'phone', 'rent'];
const looksLikeHeader = (cols: string[]): boolean => {
  const joined = cols.join(' ').toLowerCase();
  const rentNumeric = cols[4] != null && /\d/.test(cols[4]);
  return HEADER_WORDS.some((w) => joined.includes(w)) && !rentNumeric;
};

// Split a pasted block into rows. Each non-blank line is one unit; columns are TAB- (Excel/Sheets
// copy) OR comma-separated in the fixed order unit, tenant name, email, phone, rent, each trimmed. A
// leading header row is tolerated. Parsed rows land in the editable table as a preview BEFORE any POST
// (never a silent import of unseen data — a misaligned column can be fixed in the table, not discovered
// in the skipped-rows result).
const parsePaste = (text: string): Row[] => {
  const cells = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => line.split(/\t|,/).map((c) => c.trim()));
  const body = cells.length > 0 && looksLikeHeader(cells[0]) ? cells.slice(1) : cells;
  return body.map((cols) => ({
    unitLabel: cols[0] ?? '',
    tenantName: cols[1] ?? '',
    tenantEmail: cols[2] ?? '',
    tenantPhone: cols[3] ?? '',
    rent: cols[4] ?? '',
  }));
};

export const PropertyImportFlow: React.FC<{ onClose: () => void; onImported: () => void }> = ({ onClose, onImported }) => {
  const { t } = useTranslation('common');
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [startDate, setStartDate] = useState('');
  const [termMonths, setTermMonths] = useState('12');
  const [rows, setRows] = useState<Row[]>([emptyRow(), emptyRow(), emptyRow()]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/companies')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((cs: any[]) => { if (alive) setCompanies(cs.map((c) => ({ id: c.id, name: c.name || '—' }))); })
      .catch(() => { if (alive) setCompanies([]); });
    return () => { alive = false; };
  }, []);

  // Escape closes (same technique as SheetShell) — but only while the form is up; once a result is
  // shown the panel is a report the operator should dismiss deliberately via Done.
  useEffect(() => {
    if (result) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, result]);

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i: number) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));

  const applyPaste = () => {
    const parsed = parsePaste(pasteText);
    if (parsed.length === 0) { setPasteOpen(false); return; }
    // Append onto any rows the user already filled; drop the leading all-blank starter rows so a paste
    // into a fresh form doesn't leave three empty rows above it.
    setRows((rs) => {
      const kept = rs.filter((r) => r.unitLabel.trim() || r.tenantName.trim() || r.tenantEmail.trim() || r.tenantPhone.trim() || r.rent.trim());
      return [...kept, ...parsed];
    });
    setPasteText('');
    setPasteOpen(false);
  };

  const canSubmit = name.trim() && companyId && rows.some((r) => r.unitLabel.trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    const payloadRows = rows
      .filter((r) => r.unitLabel.trim() || r.tenantName.trim() || r.tenantEmail.trim() || r.tenantPhone.trim() || r.rent.trim())
      .map((r) => ({
        unitLabel: r.unitLabel.trim(),
        tenantName: r.tenantName.trim() || null,
        tenantEmail: r.tenantEmail.trim() || null,
        tenantPhone: r.tenantPhone.trim() || null,
        rent: parseRent(r.rent),
      }));
    try {
      const res = await fetch('/api/properties/bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          propertyName: name.trim(),
          propertyAddress: address.trim() || null,
          startDate: startDate || undefined,
          termMonths: termMonths.trim() ? Number(termMonths) : undefined,
          rows: payloadRows,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setError(json?.error || t('portfolio.setup.import.error')); return; }
      setResult(json as ImportResult);
    } catch {
      setError(t('portfolio.setup.import.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'h-10 px-3 rounded-lg border border-border-neutral text-sm w-full';
  const cellInputCls = 'h-10 px-2 rounded-lg border border-border-neutral text-sm w-full';

  return (
    <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center bg-black bg-opacity-50 p-0 md:p-4" data-testid="property-import-overlay">
      <div className="bg-canvas w-full md:max-w-2xl md:rounded-2xl md:border md:border-border-neutral h-full md:max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-neutral sticky top-0 bg-canvas z-10">
          <h2 className="text-lg font-bold text-text-strong m-0">{t('portfolio.setup.import.title')}</h2>
          <button type="button" data-testid="property-import-close" aria-label={t('portfolio.setup.import.close')}
            onClick={onClose} className="ms-auto min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-text-strong">
            &times;
          </button>
        </div>

        {result ? (
          <div className="flex flex-col gap-3 p-4" data-testid="property-import-result">
            <h3 className="text-base font-bold text-text-strong m-0">{t('portfolio.setup.import.result.title')}</h3>
            <ul className="flex flex-col gap-1.5 text-sm text-text-strong">
              <li data-testid="property-import-result-units">✓ {t('portfolio.setup.import.result.units', { count: result.unitsCreated ?? 0 })}</li>
              <li data-testid="property-import-result-leases">✓ {t('portfolio.setup.import.result.leases', { count: result.leasesCreated ?? 0 })}</li>
              <li data-testid="property-import-result-matched">✓ {t('portfolio.setup.import.result.matched', { count: result.contactsMatched ?? 0 })}</li>
              <li data-testid="property-import-result-created">✓ {t('portfolio.setup.import.result.created', { count: result.contactsCreated ?? 0 })}</li>
            </ul>
            {result.skipped && result.skipped.length > 0 && (
              <div className="rounded-xl border border-opt-amber/40 bg-opt-amber-bg/40 p-3" data-testid="property-import-result-skipped">
                <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-1">{t('portfolio.setup.import.result.skippedTitle')}</div>
                <ul className="flex flex-col gap-1 text-sm text-text-strong">
                  {result.skipped.map((s, i) => (
                    <li key={i} data-testid="property-import-result-skipped-row">{t('portfolio.setup.import.result.skippedRow', { unit: s.unitLabel, reason: s.reason })}</li>
                  ))}
                </ul>
              </div>
            )}
            <button type="button" data-testid="property-import-done" onClick={() => { onImported(); onClose(); }}
              className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold self-start mt-1">
              {t('portfolio.setup.import.result.done')}
            </button>
          </div>
        ) : companies !== null && companies.length === 0 ? (
          <div className="flex flex-col gap-3 p-4">
            <div className="text-sm text-text-muted">{t('portfolio.setup.import.noCompanies')}</div>
            <button type="button" onClick={onClose} data-testid="property-import-cancel"
              className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong self-start">
              {t('portfolio.setup.import.cancel')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4 p-4">
            <p className="text-sm text-text-muted m-0">{t('portfolio.setup.import.subtitle')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs text-text-muted flex flex-col gap-1">
                {t('portfolio.setup.import.name')}
                <input required autoFocus data-testid="property-import-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} dir="auto" />
              </label>
              <label className="text-xs text-text-muted flex flex-col gap-1">
                {t('portfolio.setup.import.owner')}
                <select required data-testid="property-import-owner" value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-border-neutral text-sm bg-surface w-full">
                  <option value="" disabled>{t('portfolio.setup.import.ownerPlaceholder')}</option>
                  {(companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="text-xs text-text-muted flex flex-col gap-1">
                {t('portfolio.setup.import.address')}
                <input data-testid="property-import-address" value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} dir="auto" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-text-muted flex flex-col gap-1">
                  {t('portfolio.setup.import.startDate')}
                  <input type="date" data-testid="property-import-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} dir="ltr" />
                </label>
                <label className="text-xs text-text-muted flex flex-col gap-1">
                  {t('portfolio.setup.import.termMonths')}
                  <input type="number" min="1" data-testid="property-import-term" value={termMonths} onChange={(e) => setTermMonths(e.target.value)} className={inputCls} dir="ltr" />
                </label>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-text-strong m-0">{t('portfolio.setup.import.rows')}</h3>
              <button type="button" data-testid="property-import-paste-open" onClick={() => setPasteOpen((v) => !v)}
                className="ms-auto min-h-[44px] px-3 rounded-lg border border-border-neutral text-xs font-semibold text-text-strong hover:bg-surface">
                {t('portfolio.setup.import.paste')}
              </button>
            </div>

            {pasteOpen && (
              <div className="flex flex-col gap-2 rounded-xl border border-border-neutral bg-surface p-3" data-testid="property-import-paste-panel">
                <div className="text-xs text-text-muted">{t('portfolio.setup.import.pasteHint')}</div>
                <textarea data-testid="property-import-paste-text" value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={4}
                  className="w-full p-2 rounded-lg border border-border-neutral text-sm font-mono" dir="auto" />
                <div className="flex items-center gap-2">
                  <button type="button" data-testid="property-import-paste-apply" onClick={applyPaste}
                    className="min-h-[44px] px-3 rounded-lg bg-primary text-white text-xs font-semibold">
                    {t('portfolio.setup.import.pasteApply')}
                  </button>
                  <button type="button" data-testid="property-import-paste-cancel" onClick={() => { setPasteOpen(false); setPasteText(''); }}
                    className="min-h-[44px] px-3 rounded-lg text-xs font-semibold text-text-muted">
                    {t('portfolio.setup.import.pasteCancel')}
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse" data-testid="property-import-rows">
                <thead>
                  <tr className="text-label-caps uppercase text-text-muted">
                    <th className="px-2 py-1 text-start">{t('portfolio.setup.import.col.unit')}</th>
                    <th className="px-2 py-1 text-start">{t('portfolio.setup.import.col.tenant')}</th>
                    <th className="px-2 py-1 text-start">{t('portfolio.setup.import.col.email')}</th>
                    <th className="px-2 py-1 text-start">{t('portfolio.setup.import.col.phone')}</th>
                    <th className="px-2 py-1 text-start">{t('portfolio.setup.import.col.rent')}</th>
                    <th className="px-2 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} data-testid="property-import-row">
                      <td className="px-1 py-1"><input data-testid="property-import-row-unit" value={r.unitLabel} onChange={(e) => setRow(i, { unitLabel: e.target.value })} className={cellInputCls} dir="auto" /></td>
                      <td className="px-1 py-1"><input data-testid="property-import-row-tenant" value={r.tenantName} onChange={(e) => setRow(i, { tenantName: e.target.value })} className={cellInputCls} dir="auto" /></td>
                      <td className="px-1 py-1"><input type="email" data-testid="property-import-row-email" value={r.tenantEmail} onChange={(e) => setRow(i, { tenantEmail: e.target.value })} className={cellInputCls} dir="ltr" /></td>
                      <td className="px-1 py-1"><input type="tel" data-testid="property-import-row-phone" value={r.tenantPhone} onChange={(e) => setRow(i, { tenantPhone: e.target.value })} className={cellInputCls} dir="ltr" /></td>
                      <td className="px-1 py-1"><input inputMode="decimal" data-testid="property-import-row-rent" value={r.rent} onChange={(e) => setRow(i, { rent: e.target.value })} className={cellInputCls} dir="ltr" /></td>
                      <td className="px-1 py-1">
                        <button type="button" data-testid="property-import-row-remove" aria-label={t('portfolio.setup.import.removeRow')} onClick={() => removeRow(i)}
                          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-opt-rose">&times;</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button type="button" data-testid="property-import-add-row" onClick={addRow}
              className="min-h-[44px] px-3 rounded-lg border border-border-neutral text-xs font-semibold text-text-strong hover:bg-surface self-start">
              + {t('portfolio.setup.import.addRow')}
            </button>

            {!canSubmit && (name || companyId || rows.some((r) => r.unitLabel.trim())) && (
              <div className="text-xs text-opt-rose" data-testid="property-import-requirement">{t('portfolio.setup.import.requirement')}</div>
            )}
            {error && <div className="text-xs text-opt-rose" data-testid="property-import-error">{error}</div>}

            <div className="flex items-center gap-2 mt-1">
              <button type="submit" disabled={!canSubmit || submitting} data-testid="property-import-submit"
                className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50">
                {t('portfolio.setup.import.submit')}
              </button>
              <button type="button" onClick={onClose} data-testid="property-import-cancel"
                className="min-h-[44px] px-4 rounded-lg text-sm font-semibold text-text-muted">
                {t('portfolio.setup.import.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
